import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCancelInterview, useCandidate, useUpdateInterview } from '@/hooks/use-candidates';
import { isConsentLocked } from '../lib/consent-actions';
import { cn } from '@/lib/utils';
import type { AvailabilityWindow, CandidateInterview, InterviewResult } from '@/types/candidates';
import { format, isSameDay } from 'date-fns';
import { Link2, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { INTERVIEW_RESULT_LABEL, INTERVIEW_TYPE_LABEL } from '../lib/interview-labels';
import { CandidateInterviewDialog } from './candidate-interview-dialog';

interface CandidateInterviewItemProps {
  candidateId: string;
  interview: CandidateInterview;
}

/**
 * A window names the days the interview may land on. Both ends can be the same day
 * — the day is agreed and only the hour is still open — so a single day collapses to
 * one label instead of reading "20 Aug – 20 Aug". The comparison is on the days
 * themselves, not their labels, and the year is spelled out when the ends fall in
 * different ones: "20 Aug – 20 Aug" would otherwise read as a single day.
 */
function formatWindow(window: AvailabilityWindow): string {
  const from = new Date(window.from);
  const to = new Date(window.to);
  if (isSameDay(from, to)) {
    return format(from, 'd MMM');
  }
  const pattern = from.getFullYear() === to.getFullYear() ? 'd MMM' : 'd MMM yyyy';
  return `${format(from, pattern)} – ${format(to, pattern)}`;
}

export function CandidateInterviewItem({ candidateId, interview }: CandidateInterviewItemProps) {
  const cancel = useCancelInterview(candidateId);
  const update = useUpdateInterview(candidateId);
  // HW-1055: with a candidate who revoked consent an interview can only be
  // closed — completed, no show or cancelled.
  const locked = isConsentLocked(useCandidate(candidateId).data);
  const [editOpen, setEditOpen] = useState(false);

  const hasSchedule = !!interview.scheduledAt;
  const isFlexible = !!interview.availabilityWindow;
  const awaitingConfirmation = isFlexible && interview.result === 'SCHEDULED';
  const isActive = interview.result === 'SCHEDULED';

  // The row shows type and vacancy together, so the tooltip has to carry the same string —
  // otherwise a truncated row loses the type prefix, and an unlinked interview gets no tooltip.
  const headline = `${INTERVIEW_TYPE_LABEL[interview.type]} · ${
    interview.assignment?.vacancyTitle ?? (isFlexible ? 'flexible time' : 'Not linked')
  }`;

  const setResult = (result: InterviewResult) => {
    update.mutate({ interviewId: interview.id, input: { result } });
  };

  return (
    <article
      className={cn(
        'flex items-start gap-3 rounded-md border border-border bg-background p-3',
        interview.result === 'CANCELED' && 'opacity-70',
      )}
    >
      <DateBadge interview={interview} />

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground" title={headline}>
              {headline}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {hasSchedule
                ? format(new Date(interview.scheduledAt!), 'EEE d MMM · HH:mm')
                : isFlexible
                  ? formatWindow(interview.availabilityWindow!)
                  : '—'}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Interview actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditOpen(true)} disabled={locked}>
                Edit
              </DropdownMenuItem>
              {isActive ? (
                <>
                  <DropdownMenuItem onClick={() => setResult('COMPLETED')}>
                    Mark completed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setResult('NO_SHOW')}>
                    Mark no-show
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => cancel.mutate(interview.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    Cancel
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onClick={() => setResult('SCHEDULED')} disabled={locked}>
                  Reopen (mark scheduled)
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {awaitingConfirmation ? (
            <Badge variant="secondary" className="font-normal">
              Awaiting confirmation
            </Badge>
          ) : null}
          {interview.result !== 'SCHEDULED' ? (
            <Badge
              variant={interview.result === 'CANCELED' ? 'destructive' : 'secondary'}
              className="font-normal"
            >
              {INTERVIEW_RESULT_LABEL[interview.result]}
            </Badge>
          ) : null}
          {interview.meetingLink ? (
            <a
              href={interview.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-hygge hover:underline"
            >
              <Link2 className="h-3 w-3" />
              Meeting link
            </a>
          ) : null}
        </div>

        {interview.attendees.length > 0 ? (
          <div className="flex -space-x-1.5 pt-0.5">
            {interview.attendees.slice(0, 5).map((a) => {
              const name = `${a.firstName} ${a.lastName}`.trim();
              const initials =
                `${a.firstName[0] ?? ''}${a.lastName[0] ?? ''}`.toUpperCase() || '?';
              return (
                <Avatar
                  key={a.id}
                  className="h-6 w-6 rounded-full border border-background"
                  title={name}
                >
                  {a.avatarUrl ? <AvatarImage src={a.avatarUrl} alt={name} /> : null}
                  <AvatarFallback className="rounded-full text-[9px]">{initials}</AvatarFallback>
                </Avatar>
              );
            })}
            {interview.attendees.length > 5 ? (
              <div className="flex h-6 w-6 items-center justify-center rounded-full border border-background bg-muted text-[10px] text-muted-foreground">
                +{interview.attendees.length - 5}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <CandidateInterviewDialog
        candidateId={candidateId}
        interview={editOpen ? interview : null}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </article>
  );
}

function DateBadge({ interview }: { interview: CandidateInterview }) {
  const date = interview.scheduledAt
    ? new Date(interview.scheduledAt)
    : interview.availabilityWindow
      ? new Date(interview.availabilityWindow.from)
      : null;

  if (!date) {
    return (
      <div className="flex h-[54px] w-[54px] shrink-0 flex-col items-center justify-center rounded-md bg-muted text-xs">
        <div className="font-semibold uppercase text-muted-foreground">TBD</div>
      </div>
    );
  }

  const flex = !interview.scheduledAt;
  return (
    <div className="flex h-[54px] w-[54px] shrink-0 flex-col items-center justify-center rounded-md bg-muted">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {flex ? 'FLEX' : format(date, 'EEE').toUpperCase()}
      </div>
      <div className="text-lg font-semibold leading-none text-foreground">
        {format(date, 'd')}
      </div>
    </div>
  );
}
